/**
 * Funciones tipadas de los endpoints /oauth/strava/* y /strava/*.
 *
 * Espejo de backend/app/blueprints/strava/routes.py.
 *
 * A diferencia de Google/Apple (login, id_token flow), Strava es
 * INTEGRACIÓN: el backend habla con la API de Strava en nombre del user
 * para leer actividades. Por eso el flow es authorization code, los
 * endpoints viven fuera de /auth/* y NO crean tokens Nexum nuevos.
 */

import { apiFetch } from "./client";
import {
  ApiError,
  type StravaImportStatus,
  type StravaStatus,
  type StravaSyncEnqueued,
} from "@/types/api";

/**
 * GET /strava/status — devuelve el estado de la conexión Strava del user.
 * Llamado por la home al montar para decidir qué UI renderizar (botón
 * "Conectar" vs panel "Conectado + Sincronizar").
 */
export async function getStravaStatus(): Promise<StravaStatus> {
  return apiFetch<StravaStatus>("/strava/status");
}

/**
 * GET /oauth/strava/connect — devuelve la URL de autorización de Strava
 * con state token (CSRF) ya generado por el backend. El frontend abre
 * esta URL (full redirect) y Strava redirige tras el consent al
 * `STRAVA_REDIRECT_URI` configurado en el backend.
 *
 * Flow completo:
 *   1. frontend → este endpoint → recibe authorize_url
 *   2. window.location = authorize_url
 *   3. Strava muestra consent al user
 *   4. Strava redirige a backend /oauth/strava/callback?code=&state=
 *   5. Backend verifica state, intercambia code por tokens, importa
 *      90 días de actividades, redirige a FRONTEND_URL/?strava=connected&imported=N
 *   6. La home detecta los query params y muestra feedback toast.
 */
export async function getStravaConnectUrl(): Promise<{ authorize_url: string }> {
  return apiFetch<{ authorize_url: string }>("/oauth/strava/connect");
}

/**
 * POST /strava/sync — encola una task Celery de sync incremental
 * (Sprint C Bloque 2: ahora es async).
 *
 * Devuelve 202 con `{job_id, status: "pending"}` para que el frontend
 * empiece polling a `getImportStatus()`. Si ya hay un job activo del
 * user, devuelve 409 `import_already_in_progress` con el job_id
 * existente — el frontend retoma polling sobre ese job en lugar de
 * encolar uno nuevo.
 *
 * Si el refresh_token está revocado, ese error se materializa en el
 * job (`status='failed'`, `error_message='strava_reauth_required'`),
 * NO en este endpoint — la task lo detecta cuando intenta refrescar.
 */
export async function syncStrava(): Promise<StravaSyncEnqueued> {
  return apiFetch<StravaSyncEnqueued>("/strava/sync", { method: "POST" });
}

/**
 * GET /strava/import_status — devuelve el último job de import del
 * user (cualquier status). El frontend hace polling cada 3s mientras
 * status sea pending|started.
 *
 * Devuelve `null` si el backend responde 404 `no_import_history`
 * (user nunca conectó Strava o nunca importó). El caller distingue
 * "no hay job" (null) de "job con 0 imports" (StravaImportStatus
 * con activities_imported=0).
 */
export async function getImportStatus(): Promise<StravaImportStatus | null> {
  try {
    return await apiFetch<StravaImportStatus>("/strava/import_status");
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * DELETE /oauth/strava/disconnect — revoca la autorización en Strava
 * y marca la conexión como soft-deleted (disconnected_at=now). NO borra
 * Activities ya importadas — el usuario mantiene su histórico.
 *
 * Idempotente: sin conexión existente o ya desconectada → 204 silencioso.
 */
export async function disconnectStrava(): Promise<void> {
  return apiFetch<void>("/oauth/strava/disconnect", { method: "DELETE" });
}
